import {
	Primitive,
	PrimitiveInt,
	PrimitiveInt16U,
	PrimitiveFloat,
	PrimitiveString,
	PrimitiveStringP8N,
	utilNumberToHex
} from '@sage-js/core';
import {
	OSI,
	StringP8NTable,
	FunctionDefinition,
	IClassDefinitionTableEntry,
	MapClassDefinitionTableEntryExtends,
	ClassDefinitionProperty,
	ClassDefinitionMethod,
	ISubroutineTableEntry,
	Instruction,
	InstructionAbstractCompareAndBranchIfFalseBranchTarget,
	InstructionAbstractBranchAlwaysBranchTarget,
	InstructionBCLCompareAndBranchIfFalse,
	InstructionBCLBranchAlways,
	InstructionAbstractBranchTarget,
	InstructionBCLPushConstantString,
	InstructionBCLCreateObject,
	InstructionBCLGetThisMemberFunction,
	InstructionBCLGetThisMemberValue,
	InstructionBCLSetThisMemberValue,
	InstructionBCLGetMemberFunction,
	InstructionBCLGetMemberValue,
	InstructionBCLSetMemberValue,
	InstructionBCLLineNumberAlt1,
	InstructionBCLGetGameVariable,
	InstructionBCLSetGameVariable,
	InstructionBCLCallGameFunction,
	InstructionBCLCallGameFunctionFromString,
	InstructionBCLCallGameFunctionDirect,
	InstructionBCLGetVariableValue,
	InstructionBCLSetVariableValue,
	InstructionBCLPushConstantColor8888,
	InstructionBCLPushConstantColor5551
} from '@sage-js/res-osi';

import {typed} from '../../typed';
import {
	MapSubroutineOffsetToId,
	MapFunctionOffsetToDefinitions,
	MapClassMethodOffsetToDefinitions,
	MapClassStructuresToNames,
	MapSourceRange
} from '../../types';
import {ExceptionInternal} from '../../exception/internal';
import {ExceptionInvalid} from '../../exception/invalid';
import {ASTNodeFile} from '../../ast/node/file';
import {ASTNodeComment} from '../../ast/node/comment';
import {ASTNodeArgument} from '../../ast/node/argument/class';
import {ASTNodeArgumentNumber} from '../../ast/node/argument/number';
import {ASTNodeArgumentString} from '../../ast/node/argument/string';
import {ASTNodeStatement} from '../../ast/node/statement/class';
import {
	ASTNodeStatementInstruction
} from '../../ast/node/statement/instruction';
import {ASTNodeStatementBlock} from '../../ast/node/statement/block';
import {ASTNodeStatementLine} from '../../ast/node/statement/line';
import {Assembly} from '../class';

/**
 * AssemblyDisassembler constructor.
 */
export class AssemblyDisassembler extends Assembly {
	/**
	 * Disable transforming class property and method symbols to strings.
	 */
	public disableTransformSymbols = false;

	/**
	 * Include comments for the index different things appear.
	 */
	public enableIndexComments = false;

	constructor() {
		super();
	}

	/**
	 * Reset any stateful properties.
	 */
	public reset() {
		// Does nothing at present.
	}

	/**
	 * Disassemble OSI to AST.
	 *
	 * @param osi OSI instance.
	 * @returns AST file.
	 */
	public disassemble(osi: OSI) {
		const ast = new ASTNodeFile();

		// Helper functions.
		const {entries} = ast.statements;
		const add = (...args: ASTNodeStatement[]) => {
			entries.push(...args);
		};
		const nl = () => {
			add(new ASTNodeStatementLine());
		};

		// Create mappings.
		const subroutineOffsetToId =
			this._disassembleMapSubroutineOffsetToId(osi);

		const functionsByOffset =
			this._disassembleMapFunctionOffsetToDefinitions(osi);

		const classMethodByOffset =
			this._disassembleMapClassMethodOffsetToDefinitions(osi);

		const classParents = osi.mapClassParents();

		const classMethodImplementByOffset =
			this._disassembleMapclassMethodImplementsByOffset(
				osi,
				classParents
			);

		// Create the AST statements.
		add(...this.disassembleMetadata(osi));
		nl();
		add(...this.disassembleStrings(osi));
		nl();
		add(...this.disassembleGlobals(osi));
		nl();
		add(...this.disassembleSymbols(osi));
		nl();
		add(...this.disassembleSources(osi));
		nl();
		add(...this.disassembleFunctions(
			osi,
			subroutineOffsetToId
		));
		nl();
		add(...this.disassembleClasses(
			osi,
			subroutineOffsetToId,
			classParents
		));

		for (const entry of osi.subroutines.itter()) {
			nl();
			add(...this.disassembleSubroutine(
				osi,
				entry,
				subroutineOffsetToId,
				functionsByOffset,
				classMethodByOffset,
				classMethodImplementByOffset
			));
		}

		return ast;
	}

	/**
	 * Encode OSI AST metadata.
	 *
	 * @param osi OSI instance.
	 * @returns AST statements.
	 */
	public disassembleMetadata(osi: OSI): ASTNodeStatement[] {
		const ast = this._disassembleCreateStatementBlock('metadata');

		const version = this._disassembleCreateStatementInstruction('version');

		version.arguments.entries.push(
			this._disassembleCreateArgumentFromInt(osi.header.version),
			this._disassembleCreateArgumentFromInt(osi.header.flags)
		);

		ast.statements.entries.push(version);

		return [ast];
	}

	/**
	 * Encode OSI AST strings.
	 *
	 * @param osi OSI instance.
	 * @returns AST statements.
	 */
	public disassembleStrings(osi: OSI): ASTNodeStatement[] {
		return this.disassembleStringP8NTable(
			osi.header.stringTable,
			'strings',
			'string'
		);
	}

	/**
	 * Encode OSI AST globals.
	 *
	 * @param osi OSI instance.
	 * @returns AST statements.
	 */
	public disassembleGlobals(osi: OSI): ASTNodeStatement[] {
		return this.disassembleStringP8NTable(
			osi.header.globalTable,
			'globals',
			'global'
		);
	}

	/**
	 * Encode OSI AST symbols.
	 *
	 * @param osi OSI instance.
	 * @returns AST statements.
	 */
	public disassembleSymbols(osi: OSI): ASTNodeStatement[] {
		return this.disassembleStringP8NTable(
			osi.header.symbolTable,
			'symbols',
			'symbol'
		);
	}

	/**
	 * Encode OSI AST sources.
	 *
	 * @param osi OSI instance.
	 * @returns AST statements.
	 */
	public disassembleSources(osi: OSI): ASTNodeStatement[] {
		return this.disassembleStringP8NTable(
			osi.header.sourceTable,
			'sources',
			'source'
		);
	}

	/**
	 * Encode OSI AST PrimitiveStringP8NTable.
	 *
	 * @param table Table to be encoded.
	 * @param name Block name.
	 * @param instruction Instruction names.
	 * @returns AST statements.
	 */
	public disassembleStringP8NTable(
		table: StringP8NTable,
		name: string,
		instruction: string
	): ASTNodeStatement[] {
		const ast = this._disassembleCreateStatementBlock(name);
		const {entries} = ast.statements;

		const tableEntries = table.entries;
		for (let i = 0; i < tableEntries.length; i++) {
			const entry = tableEntries[i];

			const inst = this._disassembleCreateStatementInstruction(
				instruction
			);

			inst.arguments.entries.push(
				this._disassembleCreateArgumentFromString(entry)
			);

			if (this.enableIndexComments) {
				this._disassembleSetComment(inst.comment, `${i}`);
			}

			entries.push(inst);
		}
		return [ast];
	}

	/**
	 * Encode OSI AST functions.
	 *
	 * @param osi OSI instance.
	 * @param subroutineOffsetToId Map of subroutine offsets to IDs.
	 * @returns AST statements.
	 */
	public disassembleFunctions(
		osi: OSI,
		subroutineOffsetToId: MapSubroutineOffsetToId
	): ASTNodeStatement[] {
		const ast = this._disassembleCreateStatementBlock('functions');
		ast.begin.identifier.text = 'functions';

		const {entries} = ast.statements;
		const functionEntries = osi.header.functionTable.entries;
		for (let i = 0; i < functionEntries.length; i++) {
			const func = functionEntries[i];
			entries.push(...this.disassembleFunction(
				osi,
				func,
				i,
				subroutineOffsetToId
			));
		}

		return [ast];
	}

	/**
	 * Encode OSI AST function.
	 *
	 * @param osi OSI instance.
	 * @param functionDefinition Function definition.
	 * @param index Table index.
	 * @param subroutineOffsetToId Map of subroutine offsets to IDs.
	 * @returns AST statements.
	 */
	public disassembleFunction(
		osi: OSI,
		functionDefinition: FunctionDefinition,
		index: number,
		subroutineOffsetToId: MapSubroutineOffsetToId
	): ASTNodeStatement[] {
		const ast = this._disassembleCreateStatementInstruction('function');
		if (this.enableIndexComments) {
			this._disassembleSetComment(ast.comment, `${index}`);
		}

		const offset = functionDefinition.offset.value;
		const id = subroutineOffsetToId.get(offset);
		// eslint-disable-next-line no-undefined
		if (id === undefined) {
			throw new ExceptionInvalid(
				`No subroutine at offset: ${offset}`
			);
		}

		const argEntries = ast.arguments.entries;
		argEntries.push(
			this._disassembleCreateArgumentFromString(functionDefinition.name)
		);

		argEntries.push(
			this._disassembleCreateArgumentFromInt(functionDefinition.argc)
		);

		argEntries.push(
			this._disassembleCreateArgumentFromNumber(id)
		);

		return [ast];
	}

	/**
	 * Encode OSI AST classes.
	 *
	 * @param osi OSI instance.
	 * @param subroutineOffsetToId Map of subroutine offsets to IDs.
	 * @param parents Parent mappings.
	 * @returns AST statements.
	 */
	public disassembleClasses(
		osi: OSI,
		subroutineOffsetToId: MapSubroutineOffsetToId,
		parents: MapClassDefinitionTableEntryExtends | null = null
	): ASTNodeStatement[] {
		const ast = this._disassembleCreateStatementBlock('classes');
		parents = parents || osi.mapClassParents();

		const {entries} = ast.statements;
		const classEntries = osi.header.classTable.entries;
		const nameByStructure = this._disassembleMapClassStructuresToNames(osi);
		for (const {name, structure} of classEntries) {
			nameByStructure.set(structure, name);
		}
		for (let i = 0; i < classEntries.length; i++) {
			const definition = classEntries[i];
			let parentName: PrimitiveStringP8N | null = null;
			const parent = parents.get(definition) || null;
			const extend = definition.structure.extends;
			if (parent) {
				parentName = parent.name;
			}
			else if (extend) {
				parentName = nameByStructure.get(extend) || null;
			}
			entries.push(...this.disassembleClass(
				osi,
				definition,
				i,
				subroutineOffsetToId,
				parentName
			));
		}

		return [ast];
	}

	/**
	 * Encode OSI AST class.
	 *
	 * @param osi OSI instance.
	 * @param classDefinition Class definition.
	 * @param index Table index.
	 * @param subroutineOffsetToId Map of subroutine offsets to IDs.
	 * @param parentName Parent name or null.
	 * @returns AST statements.
	 */
	public disassembleClass(
		osi: OSI,
		classDefinition: IClassDefinitionTableEntry,
		index: number,
		subroutineOffsetToId: MapSubroutineOffsetToId,
		parentName: PrimitiveStringP8N | null = null
	): ASTNodeStatement[] {
		const {name, structure} = classDefinition;

		const comments = this.enableIndexComments ? [`${index}`] : [];
		if (parentName) {
			const name = parentName.stringEncode();
			comments.push(`extends ${name}`);
		}

		const ast = this._disassembleCreateStatementBlock('class');
		this._disassembleSetComment(ast.begin.comment, comments.join(', '));

		ast.begin.arguments.entries.push(
			this._disassembleCreateArgumentFromString(name)
		);

		const {entries} = ast.statements;

		for (const property of structure.itterProperties()) {
			entries.push(...this.disassembleClassProperty(
				osi,
				property
			));
		}

		for (const method of structure.itterMethods()) {
			entries.push(...this.disassembleClassMethod(
				osi,
				method,
				subroutineOffsetToId
			));
		}

		return [ast];
	}

	/**
	 * Encode OSI AST class property.
	 *
	 * @param osi OSI instance.
	 * @param property Class property.
	 * @returns AST statements.
	 */
	public disassembleClassProperty(
		osi: OSI,
		property: ClassDefinitionProperty
	): ASTNodeStatement[] {
		const symbols = osi.header.symbolTable.entries;

		const ast = this._disassembleCreateStatementInstruction('property');

		const nameArg = this._disassembleConvertSymbolToArgumentComment(
			symbols,
			property.symbol
		);
		ast.arguments.entries.push(nameArg.arg);
		if (nameArg.comment !== null) {
			this._disassembleSetComment(ast.comment, nameArg.comment);
		}

		return [ast];
	}

	/**
	 * Encode OSI AST class property.
	 *
	 * @param osi OSI instance.
	 * @param method Class method.
	 * @param subroutineOffsetToId Map offsets to ID.
	 * @returns AST statements.
	 */
	public disassembleClassMethod(
		osi: OSI,
		method: ClassDefinitionMethod,
		subroutineOffsetToId: MapSubroutineOffsetToId
	): ASTNodeStatement[] {
		const symbols = osi.header.symbolTable.entries;

		const offset = method.offset.value;
		const id = subroutineOffsetToId.get(offset);
		// eslint-disable-next-line no-undefined
		if (id === undefined) {
			throw new ExceptionInvalid(
				`No subroutine at offset: ${offset}`
			);
		}

		const ast = this._disassembleCreateStatementInstruction('method');

		const nameArg = this._disassembleConvertSymbolToArgumentComment(
			symbols,
			method.symbol
		);
		ast.arguments.entries.push(nameArg.arg);
		if (nameArg.comment !== null) {
			this._disassembleSetComment(ast.comment, nameArg.comment);
		}

		ast.arguments.entries.push(
			this._disassembleCreateArgumentFromNumber(id)
		);

		return [ast];
	}

	/**
	 * Subroutine block from OSI.
	 *
	 * @param osi OSI instance.
	 * @param subroutineEntry Subroutine entry.
	 * @param subroutineOffsetToId Map offsets to ID.
	 * @param functionsByOffset Map function offset to function info.
	 * @param classMethodByOffset Map class offset to method info.
	 * @param classMethodImplementByOffset Map class offset to implement info.
	 * @returns AST statements.
	 */
	public disassembleSubroutine(
		osi: OSI,
		subroutineEntry: ISubroutineTableEntry,
		subroutineOffsetToId: MapSubroutineOffsetToId,
		functionsByOffset: MapFunctionOffsetToDefinitions,
		classMethodByOffset: MapClassMethodOffsetToDefinitions,
		classMethodImplementByOffset: MapClassMethodOffsetToDefinitions
	): ASTNodeStatement[] {
		const {offset, subroutine} = subroutineEntry;

		const symbols = osi.header.symbolTable.entries;

		const mapSourceRange = new Map() as MapSourceRange;

		const off = offset.value;
		const id = subroutineOffsetToId.get(off);
		// eslint-disable-next-line no-undefined
		if (id === undefined) {
			throw new ExceptionInvalid(
				`No subroutine at offset: ${off}`
			);
		}

		const r: ASTNodeStatement[] = [];

		const addLine = (comment = '') => {
			const line = this._disassembleCreateStatementLine();
			this._disassembleSetComment(line.comment, comment);
			r.push(line);
		};

		const ast = this._disassembleCreateStatementBlock('subroutine');
		ast.begin.arguments.entries.push(
			this._disassembleCreateArgumentFromNumber(id)
		);
		addLine(`address: 0x${utilNumberToHex(off)}`);

		// Loop over the instructions.
		const {entries} = ast.statements;
		for (const instruction of subroutine.instructions) {
			entries.push(...this.disassembleInstruction(
				osi,
				instruction,
				subroutineEntry,
				mapSourceRange
			));
		}

		// Add comments for source file range.
		r.push(...this._disassembleMapSourceRangeComment(osi, mapSourceRange));

		// Add comments for any function that references this.
		for (const func of functionsByOffset.get(off) || []) {
			addLine(`function: ${func.name.stringEncode()}`);
		}

		// Add comments for methods that did not inherit the subroutine.
		for (const {
			classInfo,
			method
		} of classMethodImplementByOffset.get(off) || []) {
			const symbol = symbols[method.symbol.value];
			const {name} = classInfo;
			addLine(
				`implement: ${name.stringEncode()}.${symbol.stringEncode()}`
			);
		}

		// Add comments for any methods that references this.
		for (const {
			classInfo,
			method
		} of classMethodByOffset.get(off) || []) {
			const symbol = symbols[method.symbol.value];
			const {name} = classInfo;
			addLine(`method: ${name.stringEncode()}.${symbol.stringEncode()}`);
		}

		r.push(ast);
		return r;
	}

	/**
	 * Subroutine block from OSI.
	 *
	 * @param osi OSI instance.
	 * @param instruction Subroutine instruction.
	 * @param subroutineEntry Subroutine entry.
	 * @param mapSourceRange Map sources to ranges.
	 * @returns AST statements.
	 */
	public disassembleInstruction(
		osi: OSI,
		instruction: Instruction,
		subroutineEntry: ISubroutineTableEntry,
		mapSourceRange: MapSourceRange
	) {
		const strings = osi.header.stringTable.entries;
		const symbols = osi.header.symbolTable.entries;
		const globals = osi.header.globalTable.entries;
		const classes = osi.header.classTable.entries;

		const r: ASTNodeStatement[] = [];

		const ast = this._disassembleCreateStatementInstruction(
			instruction.name
		);
		ast.arguments.entries = this.disassembleInstructionArguments(
			osi,
			instruction
		);

		let comment = '';
		let jumpedTo = false;
		let jumpedFrom = false;

		OUTER: do {
			// Create source file comment file and range info.
			{
				const cast =
					typed.cast(instruction, InstructionBCLLineNumberAlt1);
				if (cast) {
					const line = cast.arg0.value;
					const index = cast.arg1.value;
					const range = mapSourceRange.get(index);
					if (range) {
						range[0] = Math.min(range[0], line);
						range[1] = Math.max(range[1], line);
					}
					else {
						mapSourceRange.set(index, [line, line]);
					}
					break;
				}
			}

			// Lookup strings.
			{
				const cast =
					typed.cast(instruction, InstructionBCLPushConstantString);
				if (cast) {
					const index = cast.arg0.value;
					const str = strings[index];
					if (str) {
						comment = str.stringEncode();
					}
					else {
						comment = '?';
					}
					break;
				}
			}

			// Lookup class.
			{
				const cast =
					typed.cast(instruction, InstructionBCLCreateObject);
				if (cast) {
					const index = cast.arg0.value;
					const classEntry = classes[index];
					if (classEntry) {
						comment = classEntry.name.stringEncode();
					}
					else {
						comment = '?';
					}
					break;
				}
			}

			// Loopup symbols.
			for (const Instruction of [
				InstructionBCLGetThisMemberFunction,
				InstructionBCLGetThisMemberValue,
				InstructionBCLSetThisMemberValue,
				InstructionBCLGetMemberFunction,
				InstructionBCLGetMemberValue,
				InstructionBCLSetMemberValue
			]) {
				const cast = typed.cast(instruction, Instruction);
				if (!cast) {
					continue;
				}
				const index = cast.arg0.value;
				const symbol = symbols[index];
				if (symbol) {
					comment = symbol.stringEncode();
				}
				else {
					comment = '?';
				}
				break OUTER;
			}

			// Lookup strings for functions.
			{
				const cast = typed.cast(
					instruction,
					InstructionBCLCallGameFunctionFromString
				);
				if (cast) {
					const index = cast.arg0.value;
					const str = strings[index];
					if (str) {
						comment = str.stringEncode();
					}
					else {
						comment = '?';
					}
					break;
				}
			}
			for (const Instruction of [
				InstructionBCLGetGameVariable,
				InstructionBCLSetGameVariable,
				InstructionBCLCallGameFunction
			]) {
				const cast = typed.cast(instruction, Instruction);
				if (!cast) {
					continue;
				}
				const indexNS = cast.arg0.value;
				const indexName = cast.arg1.value;
				const info = ['?', '?'];

				const stringNS = strings[indexNS];
				if (stringNS) {
					info[0] = stringNS.stringEncode();
				}
				const stringName = strings[indexName];
				if (stringName) {
					info[1] = stringName.stringEncode();
				}
				comment = info.join('.');

				break OUTER;
			}

			// Mark variables as global or local.
			for (const Instruction of [
				InstructionBCLGetVariableValue,
				InstructionBCLSetVariableValue
			]) {
				const cast = typed.cast(instruction, Instruction);
				if (!cast) {
					continue;
				}
				const index = cast.arg0.value;
				// eslint-disable-next-line no-bitwise
				if (index & 0x8000) {
					comment = 'global';

					// eslint-disable-next-line no-bitwise
					const nameIndex = index ^ 0x8000;
					const globalName = globals[nameIndex];
					if (globalName) {
						comment += ` ${globalName.stringEncode()}`;
					}
					else {
						comment += ' ?';
					}
				}
				else {
					comment = 'local';
				}
				break OUTER;
			}

			// Add newline after branchers.
			for (const Instruction of [
				InstructionAbstractCompareAndBranchIfFalseBranchTarget,
				InstructionAbstractBranchAlwaysBranchTarget
			]) {
				const cast = typed.cast(instruction, Instruction);
				if (!cast) {
					continue;
				}
				// If the instruction includes an offset add warning.
				if (cast.arg1.value) {
					comment = 'WARNING: Offset from target';
				}
				jumpedFrom = true;
				break OUTER;
			}
			for (const Instruction of [
				InstructionBCLCompareAndBranchIfFalse,
				InstructionBCLBranchAlways
			]) {
				const cast = typed.cast(instruction, Instruction);
				if (!cast) {
					continue;
				}
				jumpedFrom = true;
				break OUTER;
			}

			// Add newline before targets.
			for (const Instruction of [
				InstructionAbstractBranchTarget
			]) {
				const cast = typed.cast(instruction, Instruction);
				if (!cast) {
					continue;
				}
				jumpedTo = true;
				break OUTER;
			}

			// Add a warning comment for an unexpected instructions.
			for (const Instruction of [
				InstructionBCLCallGameFunctionDirect
			]) {
				const cast = typed.cast(instruction, Instruction);
				if (!cast) {
					continue;
				}
				comment = 'WARNING: Unexpected instruction';
				break OUTER;
			}
		}
		// eslint-disable-next-line no-constant-condition
		while (false);

		if (jumpedTo) {
			const line = new ASTNodeStatementLine();
			this._disassembleSetComment(line.comment, '<-');
			r.push(line);
		}

		this._disassembleSetComment(ast.comment, comment);
		r.push(ast);

		if (jumpedFrom) {
			const line = new ASTNodeStatementLine();
			this._disassembleSetComment(line.comment, '->');
			r.push(line);
		}

		return r;
	}

	/**
	 * Arguments AST from instruction.
	 *
	 * @param osi OSI instance.
	 * @param instruction Instruction.
	 * @returns AST arguments.
	 */
	public disassembleInstructionArguments(
		osi: OSI,
		instruction: Instruction
	): ASTNodeArgument[] {
		const r: ASTNodeArgument[] = [];
		for (let i = 0; i < instruction.argc; i++) {
			const arg = instruction.argGet(i);
			r.push(
				this.disassembleInstructionArgument(
					osi,
					arg,
					i,
					instruction
				)
			);
		}
		return r;
	}

	/**
	 * Argument AST from instruction and argument.
	 *
	 * @param osi OSI instance.
	 * @param argument Instruction argument.
	 * @param argumentIndex Instruction argument index.
	 * @param instruction Instruction.
	 * @returns AST argument.
	 */
	public disassembleInstructionArgument(
		osi: OSI,
		argument: Primitive,
		argumentIndex: number,
		instruction: Instruction
	): ASTNodeArgument {
		const int = typed.cast(argument, PrimitiveInt);
		if (int) {
			let base = 10;

			// Change base for some instruction types.
			// Perhaps make this extendable.
			if (
				argumentIndex === 0 &&
				(
					typed.cast(
						instruction,
						InstructionBCLPushConstantColor8888
					) ||
					typed.cast(
						instruction,
						InstructionBCLPushConstantColor5551
					)
				)
			) {
				base = 16;
			}

			return this._disassembleCreateArgumentFromInt(int, base);
		}

		const float = typed.cast(argument, PrimitiveFloat);
		if (float) {
			return this._disassembleCreateArgumentFromFloat(float);
		}

		const str = typed.cast(argument, PrimitiveString);
		if (str) {
			return this._disassembleCreateArgumentFromString(str);
		}

		// Should never get here.
		throw new ExceptionInternal('Unhandled instruction argument type');
	}

	/**
	 * Subroutine block from OSI.
	 *
	 * @param osi OSI instance.
	 * @param mapSourceRange Map sources to ranges.
	 * @returns AST statements.
	 */
	protected _disassembleMapSourceRangeComment(
		osi: OSI,
		mapSourceRange: MapSourceRange
	) {
		const lines: ASTNodeStatementLine[] = [];
		const sources = osi.header.sourceTable.entries;

		// Add comments for source file range.
		for (const [index, range] of mapSourceRange) {
			const source = sources[index];
			const line = this._disassembleCreateStatementLine();
			const src = source ? source.stringEncode() : 'unknown';
			this._disassembleSetComment(
				line.comment,
				`source: ${index} ${src} ${range.join(':')}`
			);
			lines.push(line);
		}

		return lines;
	}

	/**
	 * Map subroutine offsets to incremental IDs.
	 *
	 * @param osi OSI instance.
	 * @returns Map object.
	 */
	protected _disassembleMapSubroutineOffsetToId(
		osi: OSI
	): MapSubroutineOffsetToId {
		// Create subroutine offset to ID mappings.
		const r = new Map() as MapSubroutineOffsetToId;
		let subroutineID = 0;
		for (const {offset} of osi.subroutines.itter()) {
			const off = offset.value;
			if (r.has(subroutineID)) {
				throw new ExceptionInvalid(`Duplicate offset: ${off}`);
			}
			r.set(off, subroutineID);
			subroutineID++;
		}
		return r;
	}

	/**
	 * Map function offsets to function definitions.
	 *
	 * @param osi OSI instance.
	 * @returns Map object.
	 */
	protected _disassembleMapFunctionOffsetToDefinitions(
		osi: OSI
	): MapFunctionOffsetToDefinitions {
		// Create subroutine offset to function mappings.
		const r = new Map() as MapFunctionOffsetToDefinitions;
		for (const func of osi.header.functionTable.entries) {
			const off = func.offset.value;
			const list = r.get(off) || [];
			list.push(func);
			r.set(off, list);
		}
		return r;
	}

	/**
	 * Map OSI class structures to names, with some sanity checking.
	 *
	 * @param osi OSI instance.
	 * @returns Map object.
	 */
	protected _disassembleMapClassStructuresToNames(osi: OSI) {
		const names = new Set<string>();
		const r = new Map() as MapClassStructuresToNames;
		for (const {name, structure} of osi.header.classTable.entries) {
			const nameV = name.value;
			if (names.has(nameV)) {
				const s = name.stringEncode();
				throw new ExceptionInvalid(`Class name not unique: ${s}`);
			}
			names.add(nameV);

			const existing = r.get(structure) || null;
			if (existing) {
				const s = name.stringEncode();
				throw new ExceptionInvalid(`Class structure not unique: ${s}`);
			}
			r.set(structure, name);
		}
		return r;
	}

	/**
	 * Map class method offsets to class definitions.
	 *
	 * @param osi OSI instance.
	 * @returns Map object.
	 */
	protected _disassembleMapClassMethodOffsetToDefinitions(
		osi: OSI
	): MapClassMethodOffsetToDefinitions {
		// Create subroutine offset to class and method mappings.
		const r = new Map() as MapClassMethodOffsetToDefinitions;
		for (const classInfo of osi.header.classTable.entries) {
			const {structure} = classInfo;
			for (const method of structure.itterMethods()) {
				const off = method.offset.value;
				const list = r.get(off) || [];
				list.push({classInfo, method});
				r.set(off, list);
			}
		}
		return r;
	}

	/**
	 * Map class method offsets to class definitions of implementation.
	 *
	 * @param osi OSI instance.
	 * @param parents Parent mappings.
	 * @returns Map object.
	 */
	protected _disassembleMapclassMethodImplementsByOffset(
		osi: OSI,
		parents: MapClassDefinitionTableEntryExtends | null = null
	) {
		parents = parents || osi.mapClassParents();

		const methodSerialize = (method: ClassDefinitionMethod) =>
			`${method.symbol.value},${method.offset.value}`;

		// Create subroutine offset to class and method mappings.
		const r = new Map() as MapClassMethodOffsetToDefinitions;
		for (const classInfo of osi.header.classTable.entries) {
			const {structure} = classInfo;
			const extend = structure.extends;
			const patentInfo = parents.get(classInfo) || null;
			const extending = patentInfo ? patentInfo.structure : extend;
			// console.log(extending);

			// List methods that may be inherited.
			const inherited = new Set<string>();
			if (extending) {
				for (const method of extending.itterMethods()) {
					inherited.add(methodSerialize(method));
				}
			}

			// Itterate methods to find those that are not being inherited.
			for (const method of structure.itterMethods()) {
				// Skip inherited.
				if (inherited.has(methodSerialize(method))) {
					continue;
				}

				// Add those not inherited to map.
				const ov = method.offset.value;
				const list = r.get(ov) || [];
				r.set(ov, list);
				list.push({
					classInfo,
					method
				});
			}
		}
		return r;
	}

	/**
	 * Create argument number from primitive int.
	 *
	 * @param value Primitive int.
	 * @param base Integer base.
	 * @returns AST argument.
	 */
	protected _disassembleCreateArgumentFromInt(
		value: PrimitiveInt,
		base = 10
	) {
		const ast = new ASTNodeArgumentNumber();
		ast.text = value.stringEncode(base);
		return ast;
	}

	/**
	 * Create argument number from primitive float.
	 *
	 * @param value Primitive float.
	 * @returns AST argument.
	 */
	protected _disassembleCreateArgumentFromFloat(
		value: PrimitiveFloat
	) {
		const ast = new ASTNodeArgumentNumber();
		ast.text = value.stringEncode();
		return ast;
	}

	/**
	 * Create argument number from regular number primitive.
	 *
	 * @param value Number primitive.
	 * @returns AST argument.
	 */
	protected _disassembleCreateArgumentFromNumber(
		value: number
	) {
		const ast = new ASTNodeArgumentNumber();
		ast.text = value.toString();
		return ast;
	}

	/**
	 * Create argument string from primitive string.
	 *
	 * @param value Primitive string.
	 * @returns AST argument.
	 */
	protected _disassembleCreateArgumentFromString(
		value: PrimitiveString
	) {
		const ast = new ASTNodeArgumentString();
		ast.text = value.stringEncode();
		return ast;
	}

	/**
	 * Set comment from text.
	 *
	 * @param comment Comment node.
	 * @param str Comment body.
	 */
	protected _disassembleSetComment(
		comment: ASTNodeComment,
		str: string
	) {
		comment.text = str ? `; ${str}` : '';
	}

	/**
	 * Create AST statement line.
	 *
	 * @returns New instance.
	 */
	protected _disassembleCreateStatementLine() {
		return new ASTNodeStatementLine();
	}

	/**
	 * Create AST statement block.
	 *
	 * @param id Identifier string.
	 * @returns New instance.
	 */
	protected _disassembleCreateStatementBlock(id: string) {
		const ast = new ASTNodeStatementBlock();
		ast.begin.identifier.text = id;
		this._disassembleSetComment(ast.end.comment, id);
		return ast;
	}

	/**
	 * Create AST statement instruction.
	 *
	 * @param id Identifier string.
	 * @returns New instance.
	 */
	protected _disassembleCreateStatementInstruction(id: string) {
		const ast = new ASTNodeStatementInstruction();
		ast.identifier.text = id;
		return ast;
	}

	/**
	 * Convert symbol to argument and comment.
	 *
	 * @param symbols Symbols list.
	 * @param symbol Symbol ID.
	 * @returns Arg an comment values.
	 */
	protected _disassembleConvertSymbolToArgumentComment(
		symbols: PrimitiveStringP8N[],
		symbol: PrimitiveInt16U
	) {
		const sym = symbols[symbol.value];
		if (this.disableTransformSymbols || !sym) {
			return {
				arg: this._disassembleCreateArgumentFromInt(symbol),
				comment: sym ? sym.stringEncode() : null
			};
		}
		return {
			arg: this._disassembleCreateArgumentFromString(sym),
			comment: this.enableIndexComments ? symbol.stringEncode() : null
		};
	}
}
